import { DragAndDropUploadData } from "@/components/DragAndDropFields";
import { useVersionPermission } from "@/hooks/useCollectionPermission";
import { DefaultPalette } from "@fluentui/react";
import {
  Button,
  Menu,
  MenuDivider,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Radio,
  RadioGroup,
} from "@fluentui/react-components";
import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  ReactElement,
  useMemo,
} from "react";
import { Accept } from "react-dropzone";
import { useNavigate } from "react-router-dom";
import { getFileVersionSchema } from "../../../../api/files";
import { Sheet, useGetSheet } from "../../../../domain/Sheet";
import { useAppNotificationManager } from "../../../AppProvider/AppNotificationProvider";
import { DialogPhaseType, useDialog } from "../../helpers/hooks";
import { ReferredFromState } from "../helper";

import { SaveType } from "@/api/files";
import {
  postDownloadTadSheetReferenceDataJSON,
  postReplaceTadSheetReferenceDataCSV,
  postReplaceTadSheetReferenceDataJSON,
  postUploadTadSheetReferenceDataCSV,
  postUploadTadSheetReferenceDataJSON,
} from "@/api/tadSheetReferences";
import {
  BulkActionRequest,
  createIdleRequest,
} from "@/components/AppProvider/AppNotificationProvider/useBulkActionToast";
import { useUploadSheetDataCSV } from "@/components/UploadData/UploadSheetData/useUploadSheetDataCSV";
import { useUploadSheetDataExcel } from "@/components/UploadData/UploadSheetData/useUploadSheetDataExcel";
import { useUploadSheetDataJSON } from "@/components/UploadData/UploadSheetData/useUploadSheetDataJSON";
import { DialogSheetDelete } from "./DialogSheetDelete";
import { DialogSheetDeleteSome } from "./DialogSheetDeleteSome";
import { useSheetDuplicateAction } from "./DialogSheetDuplicate";
import { useSelectedSheetsDuplicateAction } from "./DialogSheetDuplicateSome";

import { appRoutesDefine, useAppRouteParams } from "@/AppRoutes";

import {
  getFileVersionDataCSV,
  getFileVersionDataExcel,
  getFileVersionDataJSON,
  postFileVersionDataCSV,
} from "@/api/files";
import {
  DragAndDropFields,
  dragAndDropAcceptFiles,
  getFileType,
} from "@/components/DragAndDropFields";
import { DragAndDropAcceptFileType } from "@/components/DragAndDropFields";
import { RenderUploadedRow } from "@/components/DragAndDropFields/helper";
import { usePermissions } from "@/components/InOrganizationProvider/PermissionsProvider";
import { useVersions } from "@/components/InOrganizationProvider/VersionsProvider";
import { CSVConnectItem } from "@/components/UploadData/UploadSheetData/CSVConnectItem";
import { useGetReferredFroms } from "@/domain/ReferredFrom";
import { resolveTadTimeRange } from "@/domain/TadTimeRange";
import { translation } from "@/i18n";
import { nsSheet } from "@/i18n/locales/sheet";
import { nsUpload } from "@/i18n/locales/upload";
import { formatTadWindow } from "@/sheetRowsIO/tadsRefWindow";
import { useWarningButtonStyles } from "@/styles";
import styles from "./index.module.css";

const useTranslation = translation.use(nsSheet, nsUpload);

const accept: Accept = {
  ...dragAndDropAcceptFiles.excel,
  ...dragAndDropAcceptFiles.json,
  ...dragAndDropAcceptFiles.csv,
};

/**
 * @description 複数の場所で利用されるcontextItemsとダイアログを生成
 */
export const useDialogContextMenuItemsSheet = (sheets: Sheet[] = []) => {
  const { t } = useTranslation();
  const {
    showSuccessNotification,
    showErrorNotification,
    showInfoNotification,
  } = useAppNotificationManager();
  const { organizationId, versionId } = useAppRouteParams();
  const { versions } = useVersions();
  const version = versions.find((v) => v.id === versionId);
  const {
    hasSheetActionPermission,
    hasVersionActionPermission,
    hasTadSheetActionPermission,
    hasPermission,
  } = usePermissions();
  const hasVersionWritePermission = hasVersionActionPermission(
    version ?? null,
    "write"
  );
  const hasVersionsMetaWritePermission = hasPermission(
    "versions_meta",
    "write"
  );
  const buttonClasses = useWarningButtonStyles();

  const [uploadedFileList, setUploadedFileList] = useState<
    DragAndDropUploadData[]
  >([]);

  const [existingType, setExistingType] = useState<
    DragAndDropAcceptFileType | undefined
  >();

  const [selectedSaveType, setSelectedSaveType] =
    useState<SaveType>("overwrite");

  const [selectedSheetIds, setSelectedSheetIds] = useState([] as string[]);

  const getReferredFroms = useGetReferredFroms();
  const [isIncludingReferencedSheets, setIsIncludingReferencedSheets] =
    useState<ReferredFromState>("waiting");

  // 選択シートが被参照シートかどうかチェック
  useEffect(() => {
    if (selectedSheetIds.length === 0) {
      setIsIncludingReferencedSheets("waiting");
      return;
    }
    (async () => {
      try {
        const referredFroms = await Promise.all(
          selectedSheetIds.map((sheetId) => getReferredFroms({ sheetId }))
        );
        if (referredFroms.flat().length > 0) {
          setIsIncludingReferencedSheets("including");
        } else {
          setIsIncludingReferencedSheets("notIncluding");
        }
      } catch (error) {
        showErrorNotification(t.sheet.referencedSheetsCheckError(), error);
        setIsIncludingReferencedSheets("notIncluding");
      }
    })();
    return () => {
      setIsIncludingReferencedSheets("waiting");
    };
  }, [selectedSheetIds]);

  const [deleteDialogPhaseType, setDeleteDialogPhaseType] =
    useState<DialogPhaseType>(null);
  const [duplicateRequest, setDuplicateRequest] =
    useState<BulkActionRequest>(createIdleRequest);
  const [isDeleteSomeDialogOpen, setIsDeleteSomeDialogOpen] = useState(false);

  // 複製アクションフック（コンポーネントではなくフックとして直接呼び出す）
  useSheetDuplicateAction({
    sheets,
    duplicateRequest,
    setDuplicateRequest,
    setSelectedSheetIds,
  });
  useSelectedSheetsDuplicateAction({
    sheets,
    duplicateRequest,
    setDuplicateRequest,
    setSelectedSheetIds,
  });

  const [csvUploadDialogPhaseType, setCSVUploadDialogPhaseType] =
    useState<DialogPhaseType>(null);
  /** CSV アップロードを開始したシート。TADS参照かどうかで送信先 API が変わる。 */
  const [csvUploadTargetSheet, setCSVUploadTargetSheet] =
    useState<Sheet | null>(null);
  /** TADS参照シートの範囲置換。範囲を確認させるためファイル選択と実行の間に挟まる。 */
  const [tadsReplaceRequest, setTadsReplaceRequest] = useState<{
    sheet: Sheet;
    file: File;
  } | null>(null);
  const [tadsReplaceDialogPhaseType, setTadsReplaceDialogPhaseType] =
    useState<DialogPhaseType>(null);
  const [tadsAppendDialogPhaseType, setTadsAppendDialogPhaseType] =
    useState<DialogPhaseType>(null);
  const [uploadDialogPhaseType, setUploadDialogPhaseType] =
    useState<DialogPhaseType>(null);
  const { renderDialogs } = useDialog({
    dialogPhase: csvUploadDialogPhaseType,
    setDialogPhase: setCSVUploadDialogPhaseType,
  });
  const { renderDialogs: renderTadsReplaceDialogs } = useDialog({
    dialogPhase: tadsReplaceDialogPhaseType,
    setDialogPhase: setTadsReplaceDialogPhaseType,
  });
  const { renderDialogs: renderTadsAppendDialogs } = useDialog({
    dialogPhase: tadsAppendDialogPhaseType,
    setDialogPhase: setTadsAppendDialogPhaseType,
  });
  const {
    renderDialogs: renderUploadDialogs,
    showErrorAPIDialog: showErrorAPIDialogUpload,
  } = useDialog({
    dialogPhase: uploadDialogPhaseType,
    setDialogPhase: setUploadDialogPhaseType,
  });

  const {
    runUpload: runUploadCSV,
    isDisablePrimaryButton: isDisablePrimaryButtonCSV,
    isDisableCancelButton: isDisableCancelButtonCSV,
    getExtElement: getExtElementCSV,
    csvSheetConnectList,
    setCSVSheetConnectList,
  } = useUploadSheetDataCSV(
    uploadedFileList,
    setUploadedFileList,
    selectedSaveType
  );

  const {
    runUpload: runUploadExcel,
    isDisablePrimaryButton: isDisablePrimaryButtonExcel,
    isDisableCancelButton: isDisableCancelButtonExcel,
  } = useUploadSheetDataExcel(
    uploadedFileList,
    setUploadedFileList,
    uploadDialogPhaseType,
    setUploadDialogPhaseType,
    selectedSaveType
  );

  const {
    runUpload: runUploadJSON,
    isDisablePrimaryButton: isDisablePrimaryButtonJSON,
    isDisableCancelButton: isDisableCancelButtonJSON,
  } = useUploadSheetDataJSON(
    uploadedFileList,
    setUploadedFileList,
    uploadDialogPhaseType,
    setUploadDialogPhaseType,
    selectedSaveType
  );
  // ダイアログを呼び出せる場合はread権限があると見なす
  const getSheet = useGetSheet();

  const runDownloadSomeCSV = useCallback(
    async (sheetIds: string[]) => {
      try {
        for (const sheetId of sheetIds) {
          const sheet = await getSheet(sheetId, {
            versionId,
          });
          if (!sheet) {
            showErrorNotification(t.sheet.csv.invalidSheetError());
            return;
          }
          await getFileVersionDataCSV({
            organizationId,
            version: version!,
            sheet: sheet,
          });
        }
        showSuccessNotification(t.sheet.csv.downloadSuccess());
      } catch (error) {
        showErrorNotification(t.sheet.csv.downloadError(), error);
      }
    },
    [
      t,
      organizationId,
      version,
      versionId,
      showSuccessNotification,
      showErrorNotification,
    ]
  );

  /**
   * TADS参照シートの行を JSON でダウンロードする。CSV のダウンロードと同じく、参照に設定された
   * 時間範囲全体が対象。時間範囲は式のまま送り、api-server に参照と同じ基準時刻で解決させる
   * (クライアントで絶対時刻にすると時計のずれで参照の範囲を逸脱し 400 になりうる)。
   * 出力は参照経由の JSON 範囲置換にそのまま渡せる形式。
   */
  const runDownloadTadsJSON = useCallback(
    async (sheet: Sheet) => {
      if (!sheet.tadReferTo || !version) return;
      try {
        await postDownloadTadSheetReferenceDataJSON({
          organizationId,
          versionId,
          sheetId: sheet.id,
          timeRange: sheet.tadReferTo.timeRange,
          fileName: `${version.title}_${sheet.displayName}_data`,
        });
        showSuccessNotification(t.sheet.csv.downloadSuccess());
      } catch (error) {
        showErrorNotification(t.sheet.csv.downloadError(), error);
      }
    },
    [
      t,
      organizationId,
      version,
      versionId,
      showSuccessNotification,
      showErrorNotification,
    ]
  );

  // upload csv
  const csvUploadInputRef = useRef<HTMLInputElement | null>(null);
  const forwardToFileInput = useCallback(
    (
      e:
        | React.MouseEvent<HTMLElement, MouseEvent>
        | React.KeyboardEvent<HTMLElement>
        | undefined
    ) => {
      // inputを発火させる
      e && csvUploadInputRef.current?.dispatchEvent(new MouseEvent(e.type));
    },
    [csvUploadInputRef]
  );
  /** TADS参照シートは CSV と JSON の両方を受け付けるので、別の input を使う */
  const tadsUploadInputRef = useRef<HTMLInputElement | null>(null);
  const forwardToTadsFileInput = useCallback(
    (
      e:
        | React.MouseEvent<HTMLElement, MouseEvent>
        | React.KeyboardEvent<HTMLElement>
        | undefined
    ) => {
      e && tadsUploadInputRef.current?.dispatchEvent(new MouseEvent(e.type));
    },
    [tadsUploadInputRef]
  );

  const uploadAsCSV = useCallback(
    async (
      event: React.ChangeEvent<HTMLInputElement>,
      selectedSheetIds: string[],
      selectedSaveType: SaveType
    ) => {
      try {
        setCSVUploadDialogPhaseType("in_progress");
        const file = event.target.files?.[0];
        if (!file) {
          throw new Error(t.sheet.csv.noFileError());
        }
        const sheetId = selectedSheetIds[0];
        await postFileVersionDataCSV({
          organizationId,
          versionId,
          file,
          sheetId,
          saveType: selectedSaveType,
        });
        showSuccessNotification(t.sheet.csv.uploadSuccess());
      } catch (error) {
        showErrorNotification(t.sheet.csv.uploadError(), error);
      } finally {
        setCSVUploadDialogPhaseType(null);
        setSelectedSheetIds([]);
        setCSVUploadTargetSheet(null);
        csvUploadInputRef.current!.value = "";
      }
    },
    [t, organizationId, versionId, csvUploadInputRef]
  );

  /**
   * TADS参照シートへの追加 (追記)。行の時間軸カラムは TADS参照の時間範囲の内側である必要がある。
   * 範囲置換と同じく、相対式は受信時刻で解決し直されるので絶対時刻に固定してから送る。
   */
  const uploadAsTadsAppend = useCallback(
    async (sheet: Sheet, file: File) => {
      try {
        setTadsAppendDialogPhaseType("in_progress");
        const fileType = getFileType(file);
        if (fileType !== "csv" && fileType !== "json") {
          throw new Error(t.sheet.tadsAppend.unsupportedFile());
        }
        const timeRange = sheet.tadReferTo?.timeRange;
        if (!timeRange) {
          throw new Error(t.sheet.tadsAppend.unresolvableWindow());
        }
        // 解決できない範囲を送ると api-server が 500 を返すので、手前で分かる文言にする
        try {
          resolveTadTimeRange(timeRange, new Date());
        } catch {
          throw new Error(t.sheet.tadsAppend.unresolvableWindow());
        }
        const post =
          fileType === "json"
            ? postUploadTadSheetReferenceDataJSON
            : postUploadTadSheetReferenceDataCSV;
        const { importedRows } = await post({
          organizationId,
          versionId,
          sheetId: sheet.id,
          // 式のまま送る (理由は runTadsReplace のコメントを参照)
          timeRange,
          file,
        });
        showSuccessNotification(t.sheet.tadsAppend.success({ importedRows }));
      } catch (error) {
        showErrorNotification(t.sheet.tadsAppend.error(), error);
      } finally {
        setTadsAppendDialogPhaseType(null);
        setSelectedSheetIds([]);
        setCSVUploadTargetSheet(null);
      }
    },
    [
      t,
      organizationId,
      versionId,
      showSuccessNotification,
      showErrorNotification,
    ]
  );

  // 確認ダイアログに出す置換範囲。ダイアログを開いた時点で解決した目安で、実際に置換される
  // 範囲は api-server が受信時刻で解決する (送信は式のまま。runTadsReplace のコメントを参照)。
  const tadsReplaceWindow = useMemo(() => {
    const timeRange = tadsReplaceRequest?.sheet.tadReferTo?.timeRange;
    if (!timeRange) return null;
    try {
      const { timeRange: resolved } = resolveTadTimeRange(
        timeRange,
        new Date()
      );
      return formatTadWindow({
        startsAt: resolved.startsAt,
        endsAt: resolved.endsAt,
        timezone: timeRange.timezone,
      });
    } catch {
      return null;
    }
  }, [tadsReplaceRequest]);

  const runTadsReplace = useCallback(async () => {
    const timeRange = tadsReplaceRequest?.sheet.tadReferTo?.timeRange;
    if (!tadsReplaceRequest || !timeRange || tadsReplaceWindow === null) return;
    try {
      setTadsReplaceDialogPhaseType("in_progress");
      const fileType = getFileType(tadsReplaceRequest.file);
      if (fileType !== "csv" && fileType !== "json") {
        throw new Error(t.sheet.tadsAppend.unsupportedFile());
      }
      const post =
        fileType === "json"
          ? postReplaceTadSheetReferenceDataJSON
          : postReplaceTadSheetReferenceDataCSV;
      const { importedRows } = await post({
        organizationId,
        versionId,
        sheetId: tadsReplaceRequest.sheet.id,
        // TADS参照の時間範囲を式のまま送る。参照経由の API は参照の範囲とリクエストの範囲を
        // 受信時刻で解決し、リクエストが参照の内側にあることを要求する。クライアントで絶対時刻に
        // 固定すると、動く範囲 (例: 24時間前〜現在) では開始が受信時刻の範囲より前にはみ出し、
        // TIME_RANGE_IS_OUT_OF_REFERENCE_WINDOW の 400 になる。式のまま送れば常に一致する。
        timeRange,
        file: tadsReplaceRequest.file,
      });
      showSuccessNotification(
        t.sheet.tadsReplaceDialog.success({ importedRows })
      );
    } catch (error) {
      showErrorNotification(t.sheet.tadsReplaceDialog.error(), error);
    } finally {
      setTadsReplaceDialogPhaseType(null);
      setTadsReplaceRequest(null);
      setSelectedSheetIds([]);
      setCSVUploadTargetSheet(null);
    }
  }, [
    t,
    tadsReplaceRequest,
    tadsReplaceWindow,
    organizationId,
    versionId,
    showSuccessNotification,
    showErrorNotification,
  ]);

  const getContextMenuItemsSheet = useCallback(
    (sheet: Sheet): ReactElement => {
      const hasSheetWritePermission = hasSheetActionPermission(
        version ?? null,
        sheet.id,
        "write"
      );
      const hasSheetDeletePermission = hasSheetActionPermission(
        version ?? null,
        sheet.id,
        "delete"
      );
      // シートを削除できるか
      const isDisableDelete =
        !(hasSheetDeletePermission || hasVersionWritePermission) ||
        !hasVersionsMetaWritePermission;

      return (
        <MenuList className={styles.sheetRowMenuList}>
          <MenuItem
            onClick={async () => runDownloadSomeCSV([sheet.id])}
            disabled={false}
          >
            {sheet.referTo
              ? t.sheet.row.downloadReferencedCSV()
              : t.sheet.row.downloadCSV()}
          </MenuItem>
          {sheet.tadReferTo && (
            <MenuItem onClick={() => runDownloadTadsJSON(sheet)}>
              {t.sheet.row.downloadTadsJSON()}
            </MenuItem>
          )}
          {!sheet.referTo && (
            <>
              <Menu>
                <MenuTrigger>
                  <MenuItem>
                    {sheet.tadReferTo
                      ? t.sheet.row.uploadTadsData()
                      : t.sheet.row.uploadCSV()}
                  </MenuItem>
                </MenuTrigger>
                <MenuPopover>
                  <MenuList>
                    <MenuItem
                      disabled={
                        !hasSheetWritePermission ||
                        (sheet.tadReferTo !== null &&
                          !hasTadSheetActionPermission(
                            sheet.tadReferTo.tadSheetId,
                            "write"
                          ))
                      }
                      onClick={(e) => {
                        setSelectedSheetIds([sheet.id]);
                        setCSVUploadTargetSheet(sheet);
                        setSelectedSaveType("overwrite");
                        if (sheet.tadReferTo) {
                          forwardToTadsFileInput(e);
                        } else {
                          forwardToFileInput(e);
                        }
                      }}
                    >
                      {t.sheet.row.uploadOverwrite()}
                    </MenuItem>
                    <MenuItem
                      disabled={
                        !hasSheetWritePermission ||
                        (sheet.tadReferTo !== null &&
                          !hasTadSheetActionPermission(
                            sheet.tadReferTo.tadSheetId,
                            "write"
                          ))
                      }
                      onClick={(e) => {
                        setSelectedSheetIds([sheet.id]);
                        setCSVUploadTargetSheet(sheet);
                        setSelectedSaveType("append");
                        if (sheet.tadReferTo) {
                          forwardToTadsFileInput(e);
                        } else {
                          forwardToFileInput(e);
                        }
                      }}
                    >
                      {t.sheet.row.uploadAppend()}
                    </MenuItem>
                  </MenuList>
                </MenuPopover>
              </Menu>
              {hasVersionsMetaWritePermission && (
                <MenuItem
                  onClick={() => {
                    setSelectedSheetIds([sheet.id]);
                    setDuplicateRequest((current) =>
                      current.phase === "idle"
                        ? { phase: "requested", itemIds: [sheet.id] }
                        : current
                    );
                  }}
                  disabled={
                    !hasVersionWritePermission ||
                    !hasVersionsMetaWritePermission
                  }
                >
                  {t.sheet.row.duplicate()}
                </MenuItem>
              )}
            </>
          )}
          {hasVersionsMetaWritePermission && (
            <MenuItem
              onClick={() => {
                setSelectedSheetIds([sheet.id]);
                setDeleteDialogPhaseType("confirm");
              }}
              disabled={isDisableDelete}
              style={{
                color: isDisableDelete ? "" : DefaultPalette.red,
              }}
            >
              {t.sheet.row.delete()}
            </MenuItem>
          )}
        </MenuList>
      );
    },
    [
      t,
      version,
      runDownloadSomeCSV,
      hasSheetActionPermission,
      hasVersionWritePermission,
      hasVersionsMetaWritePermission,
      hasTadSheetActionPermission,
      forwardToFileInput,
      forwardToTadsFileInput,
      runDownloadTadsJSON,
    ]
  );

  const isDialogReady = isIncludingReferencedSheets !== "waiting";

  // ダイアログを閉じたときフォームを初期化する
  useEffect(() => {
    if (!uploadDialogPhaseType) {
      setUploadedFileList([]);
    }
  }, [uploadDialogPhaseType]);

  const getContextMenuItemsSheetsMenu = (): ReactElement => {
    const navigate = useNavigate();
    const { organizationId, versionId } = useAppRouteParams();
    const [hasVersionWritePermission] = useVersionPermission("write");

    const {
      showInfoNotification,
      showSuccessNotification,
      showErrorNotification,
    } = useAppNotificationManager();

    const isDisableEdit = !hasVersionWritePermission;
    const isDisableCreate = !hasVersionsMetaWritePermission;

    const handleDownloadExcel = useCallback(async () => {
      try {
        showInfoNotification(t.sheet.dataDownload.inProgress());
        await getFileVersionDataExcel({ organizationId, version: version! });
        showSuccessNotification(t.sheet.dataDownload.success());
      } catch (error) {
        showErrorNotification(t.sheet.dataDownload.error(), error);
      }
    }, [
      t,
      organizationId,
      version,
      showInfoNotification,
      showSuccessNotification,
      showErrorNotification,
    ]);

    const handleDownloadJSON = useCallback(async () => {
      try {
        showInfoNotification(t.sheet.dataDownload.inProgress());
        await getFileVersionDataJSON({
          organizationId,
          version: version!,
        });
        showSuccessNotification(t.sheet.dataDownload.success());
      } catch (error) {
        showErrorNotification(t.sheet.dataDownload.error(), error);
      }
    }, [
      t,
      organizationId,
      version,
      showInfoNotification,
      showSuccessNotification,
      showErrorNotification,
    ]);

    const handleDownloadYAML = useCallback(async () => {
      try {
        showInfoNotification(t.sheet.schemaDownload.inProgress());
        await getFileVersionSchema({
          organizationId,
          version: version!,
        });
        showSuccessNotification(t.sheet.schemaDownload.success());
      } catch (error) {
        showErrorNotification(t.sheet.schemaDownload.error(), error);
      }
    }, [
      t,
      organizationId,
      version,
      showInfoNotification,
      showSuccessNotification,
      showErrorNotification,
    ]);

    return useMemo(
      () => (
        <MenuList>
          {!isDisableCreate && (
            <>
              <MenuItem
                key="i1"
                style={{ maxWidth: "none" }}
                onClick={() =>
                  navigate(
                    `/organizations/${organizationId}/versions/${versionId}/sheet_create`
                  )
                }
                disabled={isDisableCreate}
              >
                {t.sheet.menu.create()}
              </MenuItem>
              <MenuDivider />
            </>
          )}
          <MenuItem
            key="i2"
            style={{ maxWidth: "none" }}
            onClick={handleDownloadExcel}
          >
            {t.sheet.menu.downloadExcel()}
          </MenuItem>
          <MenuItem
            key="i3"
            style={{ maxWidth: "none" }}
            onClick={handleDownloadJSON}
          >
            {t.sheet.menu.downloadJSON()}
          </MenuItem>
          <MenuItem
            key="i4"
            style={{ maxWidth: "none" }}
            onClick={handleDownloadYAML}
          >
            {t.sheet.menu.downloadYAML()}
          </MenuItem>
          <MenuItem
            key="i5"
            style={{ maxWidth: "none" }}
            onClick={() => setUploadDialogPhaseType("confirm")}
            disabled={isDisableEdit}
          >
            {t.sheet.menu.uploadData()}
          </MenuItem>
          <MenuDivider />
          <MenuItem
            key="i6"
            style={{ maxWidth: "none" }}
            onClick={() => {
              const tabKey = appRoutesDefine.versionTop.searchParams.tab.key;
              const tabValue =
                appRoutesDefine.versionTop.searchParams.tab.values.sheets;
              navigate(
                `/organizations/${organizationId}/versions/${versionId}?${tabKey}=${tabValue}`
              );
            }}
          >
            {t.sheet.menu.openList()}
          </MenuItem>
        </MenuList>
      ),
      [
        t,
        navigate, // 予防的措置
        versionId,
        organizationId,
        isDisableEdit,
        isDisableCreate,
        handleDownloadExcel,
        handleDownloadJSON,
        handleDownloadYAML,
      ]
    );
  };

  const renderDialogsSheet = useCallback(
    (displaySortedSheets: Sheet[] = sheets) => {
      return (
        <>
          {/* メッセージちらつきをおさえるための処理 */}
          {isDialogReady && (
            <DialogSheetDelete
              sheets={displaySortedSheets}
              selectedSheetIds={selectedSheetIds}
              setSelectedSheetIds={setSelectedSheetIds}
              isIncludingReferencedSheets={isIncludingReferencedSheets}
              dialogPhase={deleteDialogPhaseType}
              setDialogPhase={setDeleteDialogPhaseType}
            />
          )}
          {/* メッセージちらつきをおさえるための処理 */}
          {isDialogReady && (
            <DialogSheetDeleteSome
              sheets={displaySortedSheets}
              selectedSheetIds={selectedSheetIds}
              setSelectedSheetIds={setSelectedSheetIds}
              isIncludingReferencedSheets={isIncludingReferencedSheets}
              open={isDeleteSomeDialogOpen}
              setOpen={setIsDeleteSomeDialogOpen}
            />
          )}
          {renderDialogs({
            inProgress: {
              title: t.sheet.uploadDialog.csvInProgressTitle(),
            },
          })}
          {renderTadsReplaceDialogs({
            confirm: {
              title: t.sheet.tadsReplaceDialog.title(),
              displayMessage:
                tadsReplaceWindow === null
                  ? t.sheet.tadsReplaceDialog.unresolvableWindow()
                  : t.sheet.tadsReplaceDialog.message({
                      fileName: tadsReplaceRequest?.file.name ?? "",
                      window: tadsReplaceWindow,
                    }),
              buttonText: t.sheet.tadsReplaceDialog.button(),
              isThemeRed: true,
              isDisableButton:
                tadsReplaceWindow === null ||
                tadsReplaceDialogPhaseType === "in_progress",
              isDisableCancelButton:
                tadsReplaceDialogPhaseType === "in_progress",
              inProgressButtonText:
                t.sheet.tadsReplaceDialog.inProgressButton(),
              onClick: runTadsReplace,
              onClickCancel: () => setTadsReplaceRequest(null),
            },
            inProgress: {
              title: t.sheet.tadsReplaceDialog.inProgressTitle(),
            },
          })}
          {renderTadsAppendDialogs({
            inProgress: {
              title: t.sheet.tadsAppend.inProgressTitle(),
            },
          })}
          {renderUploadDialogs({
            confirm: {
              title: t.sheet.uploadDialog.title(),
              buttonText: t.sheet.uploadDialog.button(),
              onClick: () => {
                if (existingType === "csv") {
                  runUploadCSV();
                } else if (existingType === "excel") {
                  runUploadExcel();
                } else if (existingType === "json") {
                  runUploadJSON();
                }
              },
              onClickCancel: () => {
                setUploadDialogPhaseType(null);
                setUploadedFileList([]);
              },
              isDisableButton:
                existingType === "csv"
                  ? !hasVersionWritePermission ||
                    isDisablePrimaryButtonCSV ||
                    uploadDialogPhaseType !== "confirm"
                  : existingType === "excel"
                    ? !hasVersionWritePermission ||
                      isDisablePrimaryButtonExcel ||
                      uploadDialogPhaseType !== "confirm"
                    : !hasVersionWritePermission ||
                      isDisablePrimaryButtonJSON ||
                      uploadDialogPhaseType !== "confirm",
              isDisableCancelButton:
                existingType === "csv"
                  ? isDisableCancelButtonCSV
                  : existingType === "excel"
                    ? isDisableCancelButtonExcel
                    : isDisableCancelButtonJSON,
              element: (
                <>
                  <div className={styles.dialogContent}>
                    <div className={styles.dialogDescription}>
                      <p>{t.sheet.uploadDialog.description()}</p>
                      <p>{t.sheet.uploadDialog.selectFile()}</p>
                    </div>
                    <DragAndDropFields
                      accept={accept}
                      uploadedFileList={uploadedFileList}
                      setUploadedFileList={setUploadedFileList}
                      existingType={existingType}
                      setExistingType={setExistingType}
                      isDisableDragAndDrop={csvSheetConnectList.some(
                        (v) => v.writeStatus !== "waiting"
                      )}
                      fileTypeConfigs={[
                        {
                          type: "excel",
                          limit: 1,
                        },
                        {
                          type: "csv",
                          limit: 10,
                        },
                        {
                          type: "json",
                          limit: 1,
                        },
                      ]}
                      extElement={
                        <>
                          {uploadedFileList.length > 0 ? (
                            <>
                              {existingType === "csv" ? (
                                <div className={styles.text}>
                                  {t.sheet.uploadDialog.connectCSV()}
                                </div>
                              ) : (
                                <div className={styles.subtitle}>
                                  {t.upload.dataName()}
                                </div>
                              )}
                              {uploadedFileList.map(
                                ({ file, id, isDisableDelete }) => {
                                  return (
                                    <div
                                      className={styles.uploadedFileInfoBlock}
                                      key={id}
                                    >
                                      <RenderUploadedRow
                                        key={id}
                                        file={file}
                                        id={id}
                                        isDisableDeleteButton={
                                          uploadDialogPhaseType !== "confirm" ||
                                          isDisableDelete
                                        }
                                        onDelete={() => {
                                          setUploadedFileList((state) =>
                                            state.filter((v) => v.id !== id)
                                          );
                                        }}
                                        fileType={existingType}
                                      />
                                      {existingType === "csv" && (
                                        <>
                                          <div className={styles.uploadedData}>
                                            <div className={styles.info}>
                                              <CSVConnectItem
                                                displaySortedSheets={
                                                  displaySortedSheets
                                                }
                                                fileId={id}
                                                uploadedFileList={
                                                  uploadedFileList
                                                }
                                                csvSheetConnectList={
                                                  csvSheetConnectList
                                                }
                                                setCSVSheetConnectList={
                                                  setCSVSheetConnectList
                                                }
                                              />
                                            </div>
                                          </div>
                                          <div className={styles.uploadedData}>
                                            {getExtElementCSV(id)}
                                          </div>
                                        </>
                                      )}
                                    </div>
                                  );
                                }
                              )}
                            </>
                          ) : null}
                          <>
                            <div className={styles.subtitle}>
                              {t.sheet.uploadDialog.saveTypeLabel()}
                            </div>
                            <RadioGroup
                              onChange={(_, data) => {
                                setSelectedSaveType(data.value as SaveType);
                              }}
                              defaultValue={selectedSaveType}
                            >
                              <Radio
                                style={{
                                  alignItems: "center",
                                }}
                                value="overwrite"
                                label={t.sheet.uploadDialog.saveTypeOverwrite()}
                              ></Radio>
                              <Radio
                                style={{
                                  alignItems: "center",
                                }}
                                value="append"
                                label={t.sheet.uploadDialog.saveTypeAppend()}
                              ></Radio>
                            </RadioGroup>
                            {existingType && (
                              <div className={styles.infoMsg}>
                                ※
                                {existingType === "excel"
                                  ? t.sheet.uploadDialog.noteExcel()
                                  : existingType === "json"
                                    ? t.sheet.uploadDialog.noteJSON()
                                    : t.sheet.uploadDialog.noteCSV()}
                              </div>
                            )}
                          </>
                        </>
                      }
                    />
                  </div>
                </>
              ),
            },
            inProgress: {
              title: t.sheet.uploadDialog.inProgressTitle(),
            },
          })}
          <input
            type="file"
            ref={csvUploadInputRef}
            accept={".csv"}
            onChange={async (e) => {
              await uploadAsCSV(e, selectedSheetIds, selectedSaveType);
            }}
            style={{ display: "none" }}
          />
          <input
            type="file"
            ref={tadsUploadInputRef}
            accept={".csv,.json"}
            onChange={async (e) => {
              const file = e.target.files?.[0] ?? null;
              e.target.value = "";
              if (!file || !csvUploadTargetSheet?.tadReferTo) return;
              if (selectedSaveType === "append") {
                await uploadAsTadsAppend(csvUploadTargetSheet, file);
                return;
              }
              // 上書きは範囲置換なので、置換範囲を確認させてから実行する
              setTadsReplaceRequest({ sheet: csvUploadTargetSheet, file });
              setTadsReplaceDialogPhaseType("confirm");
            }}
            style={{ display: "none" }}
          />
        </>
      );
    },
    [
      t,
      sheets,
      selectedSheetIds,
      deleteDialogPhaseType,
      isDeleteSomeDialogOpen,
      csvUploadDialogPhaseType,
      runUploadCSV,
      runUploadExcel,
      runUploadJSON,
      getExtElementCSV,
      hasVersionWritePermission,
      uploadDialogPhaseType,
      isDisablePrimaryButtonCSV,
      isDisablePrimaryButtonExcel,
      isDisablePrimaryButtonJSON,
      isIncludingReferencedSheets,
      uploadedFileList,
      existingType,
      csvUploadTargetSheet,
      tadsReplaceRequest,
      tadsReplaceWindow,
      tadsReplaceDialogPhaseType,
      runTadsReplace,
      uploadAsTadsAppend,
      renderTadsAppendDialogs,
    ]
  );

  const isDisabledDownload =
    selectedSheetIds.length === 0 ||
    !selectedSheetIds.every((id) =>
      hasSheetActionPermission(version ?? null, id, "read")
    );
  const isDisableDelete =
    selectedSheetIds.length === 0 ||
    // 全てのシートに対して削除権限がある、またはバージョンの書き込み権限がある、がどちらもfalseの場合は削除できない
    (!selectedSheetIds.every((id) =>
      hasSheetActionPermission(version ?? null, id, "delete")
    ) &&
      !hasVersionWritePermission);
  const isDisableDuplicate =
    selectedSheetIds.length === 0 || !hasVersionWritePermission;

  const renderCSVDownloadButton = useCallback(
    () => (
      <Button
        onClick={async () => {
          runDownloadSomeCSV(selectedSheetIds);
        }}
        disabled={isDisabledDownload}
      >
        CSVダウンロード
      </Button>
    ),
    [selectedSheetIds, isDisabledDownload]
  );
  const renderDeleteSheetsButton = useCallback(
    () => (
      <Button
        onClick={() => {
          if (selectedSheetIds.length === 1) {
            setDeleteDialogPhaseType("confirm");
          } else {
            setIsDeleteSomeDialogOpen(true);
          }
        }}
        disabled={isDisableDelete || !hasVersionsMetaWritePermission}
        className={buttonClasses.warning}
      >
        削除
      </Button>
    ),
    [
      selectedSheetIds,
      isDisableDelete,
      hasVersionsMetaWritePermission,
      setDeleteDialogPhaseType,
      setIsDeleteSomeDialogOpen,
    ]
  );
  const renderDuplicateSheetsButton = useCallback(
    () => (
      <Button
        onClick={() => {
          setDuplicateRequest((current) =>
            current.phase === "idle"
              ? { phase: "requested", itemIds: selectedSheetIds }
              : current
          );
        }}
        disabled={isDisableDuplicate || !hasVersionsMetaWritePermission}
      >
        複製
      </Button>
    ),
    [selectedSheetIds, isDisableDuplicate, hasVersionsMetaWritePermission]
  );
  const renderExcelDownloadButton = useCallback(() => {
    return (
      <Button
        onClick={async () => {
          try {
            await getFileVersionDataExcel({
              organizationId,
              version: version!,
            });
            showSuccessNotification(t.sheet.dataDownload.success());
          } catch (error) {
            showErrorNotification(t.sheet.dataDownload.error(), error);
          }
        }}
      >
        Excelで一括ダウンロード
      </Button>
    );
  }, [t, organizationId, version]);
  const renderUploadButton = useCallback(() => {
    return (
      <Button
        onClick={() => {
          setUploadDialogPhaseType("confirm");
        }}
      >
        アップロード
      </Button>
    );
  }, []);
  const renderDownloadMenu = useCallback(() => {
    return (
      <Menu>
        <MenuTrigger>
          <Button>ダウンロード</Button>
        </MenuTrigger>
        <MenuPopover>
          <MenuList>
            <MenuItem
              key="i1"
              onClick={async () => {
                try {
                  showInfoNotification(t.sheet.dataDownload.inProgress());
                  await getFileVersionDataExcel({
                    organizationId,
                    version: version!,
                  });
                  showSuccessNotification(t.sheet.dataDownload.success());
                } catch (error) {
                  showErrorNotification(t.sheet.dataDownload.error(), error);
                }
              }}
            >
              Excel（全シート）
            </MenuItem>
            <MenuItem
              key="i2"
              onClick={async () => {
                runDownloadSomeCSV(selectedSheetIds);
              }}
              disabled={selectedSheetIds.length === 0}
            >
              CSV
            </MenuItem>
            <MenuItem
              key="i3"
              onClick={async () => {
                try {
                  showInfoNotification(t.sheet.dataDownload.inProgress());
                  await getFileVersionDataJSON({
                    organizationId,
                    version: version!,
                  });
                  showSuccessNotification(t.sheet.dataDownload.success());
                } catch (error) {
                  showErrorNotification(t.sheet.dataDownload.error(), error);
                }
              }}
            >
              JSON（全シート）
            </MenuItem>
          </MenuList>
        </MenuPopover>
      </Menu>
    );
  }, [selectedSheetIds, t, organizationId, version, runDownloadSomeCSV]);
  return {
    renderDialogsSheet,
    getContextMenuItemsSheet,
    getContextMenuItemsSheetsMenu,
    renderDeleteSheetsButton,
    renderDuplicateSheetsButton,
    renderUploadButton,
    selectedSheetIds,
    setSelectedSheetIds,
    renderCSVDownloadButton,
    renderExcelDownloadButton,
    renderDownloadMenu,
  };
};
